<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋對話內容..."
          @input="handleSearch"
        />
      </div>
      <div class="filter-group">
        <input
          v-model="filterSessionId"
          type="text"
          class="form-control"
          placeholder="會話 ID..."
          @input="handleFilterChange"
        />
      </div>
      <div class="filter-group">
        <input
          v-model="filterStartDate"
          type="date"
          class="form-control"
          placeholder="開始日期"
          @change="handleFilterChange"
        />
      </div>
      <div class="filter-group">
        <input
          v-model="filterEndDate"
          type="date"
          class="form-control"
          placeholder="結束日期"
          @change="handleFilterChange"
        />
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchConversations">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>會話 ID</th>
            <th>訊息數</th>
            <th>最新用戶訊息</th>
            <th>最新 AI 回應</th>
            <th>信心度</th>
            <th>總成本（台幣）</th>
            <th>時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="conversations.length === 0">
            <td colspan="8" class="empty-state">
              沒有找到對話記錄
            </td>
          </tr>
          <tr v-for="conversation in conversations" :key="conversation.id">
            <td>{{ conversation.session_id }}</td>
            <td>
              <span class="badge badge-secondary">
                {{ conversation.message_count || 1 }} 則
              </span>
            </td>
            <td class="message-cell">{{ truncateText(conversation.user_message, 50) }}</td>
            <td class="message-cell">{{ truncateText(conversation.ai_reply, 50) }}</td>
            <td>
              <span v-if="conversation.confidence != null" class="badge badge-info">
                {{ (parseFloat(conversation.confidence) * 100).toFixed(0) }}%
              </span>
              <span v-else>-</span>
            </td>
            <td>{{ formatTwdFromStored(conversation.total_cost_twd || conversation.cost_twd || 0) }}</td>
            <td>{{ formatDate(conversation.created_at) }}</td>
            <td>
              <router-link
                :to="{ name: 'ChatMateConversationDetail', params: { sessionId: conversation.session_id } }"
                class="btn btn-sm btn-secondary"
              >
                <i class="ri-eye-line"></i>
                查看詳情
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 分頁 -->
      <ListPagination
        v-if="pagination.totalPages > 1"
        :page="currentPage"
        :total-pages="pagination.totalPages"
        :total="pagination.totalItems"
        @change="goToPage"
      />
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { chatmateService } from '@/api/modules/chatmate.js';
import { showError } from '@/utils/swal.js';
import { formatTwdFromStored } from '@/utils/currency.js';

export default {
  name: 'ChatMateConversationsList',
  setup() {
    const { pageTitle } = usePageTitle('對話歷史');
    const conversations = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const filterSessionId = ref('');
    const filterStartDate = ref('');
    const filterEndDate = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const pagination = reactive({
      totalItems: 0,
      totalPages: 1
    });

    const fetchConversations = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value
        };

        if (searchQuery.value) {
          params.search = searchQuery.value;
        }

        if (filterSessionId.value) {
          params.sessionId = filterSessionId.value;
        }

        if (filterStartDate.value) {
          params.startDate = filterStartDate.value;
        }

        if (filterEndDate.value) {
          params.endDate = filterEndDate.value;
        }

        const response = await chatmateService.getConversations(params);

        if (response && response.success && response.data) {
          conversations.value = response.data.items || [];
          pagination.totalItems = response.data.pagination?.totalItems || 0;
          pagination.totalPages = response.data.pagination?.totalPages || 1;
        } else {
          error.value = response?.message || '獲取對話列表失敗';
        }
      } catch (err) {
        console.error('獲取對話列表失敗:', err);
        error.value = err.response?.data?.message || err.message || '獲取對話列表失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      currentPage.value = 1;
      fetchConversations();
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchConversations();
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= pagination.totalPages) {
        currentPage.value = page;
        fetchConversations();
      }
    };

    const truncateText = (text, length) => {
      if (!text) return '-';
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW');
    };

    onMounted(() => {
      fetchConversations();
    });

    return {
      pageTitle,
      conversations,
      loading,
      error,
      searchQuery,
      filterSessionId,
      filterStartDate,
      filterEndDate,
      currentPage,
      pagination,
      fetchConversations,
      handleSearch,
      handleFilterChange,
      goToPage,
      truncateText,
      formatDate,
      formatTwdFromStored
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper {
  background: white;
  border-radius: 8px;
  overflow-x: auto;
  overflow-y: visible;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  -webkit-overflow-scrolling: touch;
}

table {
  width: 100%;
  min-width: 1000px;
  border-collapse: collapse;
}

thead {
  background-color: #f5f5f5;
}

th,
td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

th {
  font-weight: 600;
  color: #333;
}

tbody tr:hover {
  background-color: #f9f9f9;
}

.empty-state {
  text-align: center;
  color: #999;
  padding: 40px;
}

.message-cell {
  max-width: 200px;
  word-break: break-word;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 20px;
}
</style>
